import { useState } from "react";
import { Link } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Users, Mail, ArrowLeft, CheckCircle2 } from "lucide-react";

export default function Recovery() {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email) return toast.error("Preencha seu email");

    setLoading(true);
    try {
      const { error } = await supabase.auth.resetPasswordForEmail(email.trim().toLowerCase(), {
        redirectTo: `${window.location.origin}/login`,
      });

      if (error) throw error;

      setSubmitted(true);
      toast.success("Instruções enviadas para o seu email!");
    } catch (error: any) {
      console.error("Recovery error:", error);
      toast.error(error.message || "Erro ao solicitar recuperação de senha.");
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-slate-50 p-6">
        <div className="w-full max-w-md bg-white p-8 rounded-[2rem] shadow-2xl border border-slate-100 text-center animate-in fade-in zoom-in duration-300">
          <div className="w-20 h-20 bg-emerald-50 rounded-full flex items-center justify-center mx-auto mb-6">
            <CheckCircle2 className="h-10 w-10 text-emerald-600" />
          </div>
          <h2 className="text-2xl font-bold text-slate-900 mb-2">Verifique seu email</h2>
          <p className="text-slate-600 mb-8">
            Enviamos um link de recuperação para <strong>{email}</strong>. 
            Verifique sua caixa de entrada e spam.
          </p>
          <Button asChild className="w-full rounded-xl h-12">
            <Link to="/login">Voltar para Login</Link>
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen bg-slate-50/50 items-center justify-center p-6">
      <div className="w-full max-w-[420px] space-y-8 animate-in fade-in slide-in-from-bottom-4 duration-700">
        <div className="flex flex-col items-center gap-4 mb-8">
          <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-blue-600 shadow-xl shadow-blue-200">
            <Users className="h-7 w-7 text-white" />
          </div>
          <span className="text-2xl font-bold tracking-tight text-slate-900">RH Simples</span>
        </div>

        <div className="space-y-2 text-center">
          <h2 className="text-3xl font-bold tracking-tight text-slate-900">Recuperar senha</h2>
          <p className="text-slate-500">Esqueceu sua senha? Não se preocupe, vamos te ajudar.</p>
        </div>

        <div className="bg-white p-8 rounded-[2rem] border border-slate-100 shadow-2xl shadow-slate-200/50">
          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="space-y-2">
              <Label htmlFor="email" className="text-sm font-semibold text-slate-700 ml-1">Email cadastrado</Label>
              <div className="relative">
                <Input
                  id="email"
                  type="email"
                  placeholder="exemplo@empresa.com"
                  className="h-12 px-4 pl-11 rounded-xl border-slate-200 focus:border-blue-500 focus:ring-blue-500/10 transition-all bg-slate-50/30"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                />
                <Mail className="absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
              </div>
            </div>

            <Button 
              type="submit" 
              className="w-full h-12 rounded-xl bg-blue-600 hover:bg-blue-700 text-white font-bold text-base shadow-lg shadow-blue-200/50 transition-all disabled:opacity-70" 
              disabled={loading}
            >
              {loading ? (
                <div className="flex items-center gap-2">
                  <div className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent" />
                  <span>Enviando...</span>
                </div>
              ) : (
                "Enviar link de recuperação"
              )}
            </Button>
            
            <Link to="/login" className="flex items-center justify-center gap-2 text-sm font-semibold text-slate-500 hover:text-blue-600 transition-colors pt-2">
              <ArrowLeft className="h-4 w-4" />
              Voltar para login
            </Link>
          </form>
        </div>
      </div>
    </div>
  );
}
